import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { durationColor } from '@/lib/badgeColors';
import { Zap, Headphones, Package, ArrowLeft, ArrowRight, Play, Download, Sun, Moon, FileText, BookOpen, ExternalLink } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AudioPlayer from '@/components/AudioPlayer';
import { API_BASE } from '@/lib/api';

export default function CustomerPortal() {
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState(localStorage.getItem('sparky_customer_token'));
  const [entitlements, setEntitlements] = useState([]);
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [linkSent, setLinkSent] = useState(false);

  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get('token');
    if (urlToken) {
      api.get(`/customer/verify/${urlToken}`).then(r => {
        localStorage.setItem('sparky_customer_token', r.data.token);
        setToken(r.data.token);
        window.history.replaceState({}, '', '/portal');
      }).catch(() => toast.error('Invalid or expired link'));
    }
  }, []);

  useEffect(() => {
    if (token) {
      api.get('/portal/entitlements').then(r => {
        setEntitlements(r.data.entitlements || []);
        setProducts(r.data.products || []);
      }).catch(() => { localStorage.removeItem('sparky_customer_token'); setToken(null); });
    }
  }, [token]);

  const requestAccess = async () => {
    if (!email) { toast.error('Enter your email'); return; }
    setSending(true);
    try {
      await api.post('/customer/magic-link', { email });
      setLinkSent(true);
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setSending(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="portal-login">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 space-y-4 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto"><img src="/logo.png" alt="Wayfinder" className="w-14 h-14 rounded-2xl" /></div>
            <h1 className="text-2xl font-bold font-['Outfit']">Access Your Guides</h1>
            {linkSent ? (
              <div className="space-y-3" data-testid="portal-link-sent">
                <div className="w-12 h-12 rounded-full bg-[#1A9DA5]/10 flex items-center justify-center mx-auto">
                  <ArrowRight className="w-6 h-6 text-[#1A9DA5]" />
                </div>
                <h2 className="text-lg font-semibold">Check your email</h2>
                <p className="text-muted-foreground text-sm">If you have purchased guides with <strong>{email}</strong>, we've sent a login link to your inbox. Click the link in the email to access your library.</p>
                <p className="text-muted-foreground text-xs">Don't see it? Check your spam folder.</p>
                <Button variant="outline" size="sm" onClick={() => setLinkSent(false)} data-testid="portal-try-again-btn">Try a different email</Button>
              </div>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">Enter the email you used at checkout. We'll send you a login link.</p>
                <div className="space-y-2 text-left">
                  <Label>Email</Label>
                  <Input data-testid="portal-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@business.com" onKeyDown={e => e.key === 'Enter' && requestAccess()} />
                </div>
                <Button data-testid="portal-access-btn" onClick={requestAccess} disabled={sending} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                  {sending ? 'Sending...' : 'Send Login Link'}
                </Button>
                <Link to="/" className="text-sm text-muted-foreground hover:text-[#1A9DA5] transition-colors inline-flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" />Back to Store
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="customer-portal">
      <header className="sticky top-0 z-50 border-b glass-effect bg-background/80">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" /><span className="font-bold font-['Outfit']">My Guides</span></Link>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <Button variant="ghost" size="sm" onClick={() => { localStorage.removeItem('sparky_customer_token'); setToken(null); }}>Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {selectedProduct ? (
          <div className="space-y-6" data-testid="portal-product-view">
            <Button variant="ghost" onClick={() => setSelectedProduct(null)} data-testid="portal-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />Back to Library
            </Button>

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="capitalize">{selectedProduct.type}</Badge>
                  {selectedProduct.category && <Badge variant="outline">{selectedProduct.category}</Badge>}
                  {selectedProduct.duration_minutes && <Badge variant="outline" className={`${durationColor(selectedProduct.duration_minutes).bg} ${durationColor(selectedProduct.duration_minutes).text} ${durationColor(selectedProduct.duration_minutes).border}`}>{selectedProduct.duration_minutes} min</Badge>}
                </div>
                <h1 className="text-3xl font-bold font-['Outfit']">{selectedProduct.title}</h1>
                {selectedProduct.subtitle && <p className="text-muted-foreground mt-1">{selectedProduct.subtitle}</p>}
              </div>
            </div>

            {selectedProduct.type === 'package' && selectedProduct.included_guides?.length > 0 && (
              <Card data-testid="package-guides-list">
                <CardHeader>
                  <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                    <Package className="w-4 h-4 text-[#1A9DA5]" />
                    Included Guides ({selectedProduct.included_guides.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {products.filter(p => selectedProduct.included_guides.includes(p.id)).map(g => (
                      <button
                        key={g.id}
                        onClick={() => setSelectedProduct(g)}
                        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-accent/50 transition-colors"
                        data-testid={`package-guide-${g.id}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                          <Headphones className="w-5 h-5 text-[#1A9DA5]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{g.title}</p>
                          <p className="text-xs text-muted-foreground"><span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] mr-1 ${durationColor(g.duration_minutes).bg} ${durationColor(g.duration_minutes).text}`}>{g.duration_minutes} min</span>{g.category}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedProduct.type === 'guide' && (
              <AudioPlayer product={selectedProduct} />
            )}

            {selectedProduct.pdf_asset_id && (
              <Card className="border-l-4 border-l-blue-500" data-testid="portal-pdf-download">
                <CardContent className="p-5 flex items-center gap-4">
                  <FileText className="w-10 h-10 text-red-500 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-base font-semibold font-['Outfit']">PDF Document</h3>
                    <p className="text-sm text-muted-foreground">{selectedProduct.pdf_filename || 'Guide document'}</p>
                  </div>
                  <a href={`${API_BASE}/assets/${selectedProduct.pdf_asset_id}/pdf`} download data-testid="portal-pdf-download-btn">
                    <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                      <Download className="w-4 h-4 mr-1" />Download PDF
                    </Button>
                  </a>
                </CardContent>
              </Card>
            )}

            {selectedProduct.external_link_url && (
              <Card className="border-l-4 border-l-[#1A9DA5]" data-testid="portal-external-link">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                    <ExternalLink className="w-5 h-5 text-[#1A9DA5]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold font-['Outfit']">{selectedProduct.external_link_label || 'Recommended Resource'}</h3>
                    <p className="text-sm text-muted-foreground">Companion tool for this guide</p>
                  </div>
                  <a href={selectedProduct.external_link_url} target="_blank" rel="noopener noreferrer" data-testid="portal-external-link-btn">
                    <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                      <ExternalLink className="w-4 h-4 mr-1" />{selectedProduct.external_link_label || 'Recommended Resource'}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="about" className="w-full">
              <TabsList>
                <TabsTrigger value="about" data-testid="portal-tab-about">
                  <BookOpen className="w-3.5 h-3.5 mr-1" />About
                </TabsTrigger>
                <TabsTrigger value="transcript" data-testid="portal-tab-transcript">
                  <FileText className="w-3.5 h-3.5 mr-1" />Transcript
                </TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="mt-4 space-y-4">
                {selectedProduct.short_description && (
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-sm leading-relaxed">{selectedProduct.short_description}</p>
                    </CardContent>
                  </Card>
                )}

                {selectedProduct.outcomes?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-base font-['Outfit']">What You'll Learn</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {selectedProduct.outcomes.map((o, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#1A9DA5]/10 flex items-center justify-center shrink-0 mt-0.5">
                            <Play className="w-2.5 h-2.5 text-[#1A9DA5]" />
                          </div>
                          <span className="text-sm">{o}</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {selectedProduct.long_description && (
                  <Card>
                    <CardHeader><CardTitle className="text-base font-['Outfit']">About This Guide</CardTitle></CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{selectedProduct.long_description}</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="transcript" className="mt-4 space-y-4">
                {selectedProduct.teaser_script && (
                  <Card className="border-l-4 border-l-[#1A9DA5]" data-testid="portal-teaser-transcript">
                    <CardHeader><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><Play className="w-4 h-4 text-[#1A9DA5]" />Teaser Script</CardTitle></CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{selectedProduct.teaser_script}</p>
                    </CardContent>
                  </Card>
                )}

                {selectedProduct.chaptered_script?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedProduct.chaptered_script.map((ch, i) => (
                      <Card key={i} data-testid={`portal-chapter-transcript-${i}`}>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm font-['Outfit'] flex items-center gap-2">
                            <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center p-0 text-xs">{ch.chapter || i + 1}</Badge>
                            {ch.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{ch.content}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : selectedProduct.transcript_full ? (
                  <Card data-testid="portal-full-transcript">
                    <CardHeader><CardTitle className="text-base font-['Outfit']">Full Transcript</CardTitle></CardHeader>
                    <CardContent>
                      <ScrollArea className="max-h-[500px]">
                        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{selectedProduct.transcript_full}</p>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No transcript available yet.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold font-['Outfit']">My Library</h1>
            {products.length === 0 ? (
              <Card><CardContent className="p-12 text-center">
                <Headphones className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">No guides yet</p>
                <p className="text-sm text-muted-foreground mb-4">Purchase a guide to get started</p>
                <Link to="/"><Button className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">Browse Guides</Button></Link>
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map(p => (
                  <Card key={p.id} className="cursor-pointer hover:border-[#1A9DA5]/50 hover:shadow-md transition-all" onClick={() => setSelectedProduct(p)}>
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center mb-3">
                        {p.type === 'package' ? <Package className="w-5 h-5 text-[#1A9DA5]" /> : <Headphones className="w-5 h-5 text-[#1A9DA5]" />}
                      </div>
                      <h3 className="font-semibold text-base font-['Outfit'] mb-1">{p.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{p.short_description}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className={`text-xs ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text} ${durationColor(p.duration_minutes).border}`}>{p.duration_minutes} min</Badge>
                        {p.pdf_asset_id && <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600"><FileText className="w-3 h-3 mr-1" />PDF</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
